import { getNormalizedError } from '@/shared/api/lib';
import { useFacilityScope } from '@/shared/lib/hooks/use-facility-scope';
import { formatNumber } from '@/shared/lib/number';
import { DonutChart, Loader, TooltipElement, Typography } from '@/shared/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { Dayjs } from 'dayjs';
import { type FC } from 'react';
import { getPayerDistributionQueryOptions } from '../api/get-payer-distribution.query';

interface PayerDistributionChartProps {
  dateRange: {
    from: Dayjs;
    to: Dayjs;
  };
}

export const PayerDistribution: FC<PayerDistributionChartProps> = ({
  dateRange,
}) => {
  const { scopedFacilityId } = useFacilityScope();
  const { data, isLoading, error } = useQuery(
    getPayerDistributionQueryOptions({
      from: dateRange.from.startOf('month').format('YYYY-MM-DD'),
      to: dateRange.to.endOf('month').format('YYYY-MM-DD'),
      facilitiesIds: scopedFacilityId ? [scopedFacilityId] : undefined,
    })
  );

  const noPayerTypes = getNormalizedError(error).statusCode == 422;

  if (noPayerTypes) {
    return (
      <div className="border-neutral-5 flex w-[200px] flex-col items-center gap-2 border bg-white p-3">
        <div className="flex items-center gap-x-2 pb-3">
          <Typography variant="h5" weight="medium">
            Payer Distribution
          </Typography>
          <TooltipElement content="Share of residents paying privately vs via Medicaid" />
        </div>

        <div className="flex h-full items-center">
          <div className="flex flex-col items-center gap-1 text-center">
            <Typography className="text-neutral-8">
              Payer type not set
            </Typography>
            {scopedFacilityId && (
              <Link
                to="/facilities/$facilityId/edit"
                className="text-primary-text underline"
                params={{ facilityId: String(scopedFacilityId) }}
              >
                Set it in Edit Facility
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!isLoading && !data) {
    return (
      <div className="border-neutral-5 flex w-[200px] flex-col items-center gap-2 border bg-white p-3">
        <div className="flex items-center gap-x-2 pb-3">
          <Typography variant="h5" weight="medium">
            Payer Distribution
          </Typography>
          <TooltipElement content="Share of residents paying privately vs via Medicaid" />
        </div>

        <Typography className="flex flex-1 items-center">No data</Typography>
      </div>
    );
  }

  const medicaidPct = data?.medicaid.currentPct;
  const privatePct = data?.private.currentPct;
  const medicaidCount = data?.medicaid.count;
  const privateCount = data?.private.count;

  const privateNum = privatePct ?? 50;
  const medicaidNum = medicaidPct ?? 50;
  const privateCountVal =
    typeof privateCount === 'number' ? formatNumber(privateCount) : 'N/A';
  const medicaidCountVal =
    typeof medicaidCount === 'number' ? formatNumber(medicaidCount) : 'N/A';

  const chartData = {
    labels: [
      typeof medicaidPct === 'number' ? 'Medicaid' : 'N/A',
      typeof privatePct === 'number' ? 'Private' : 'N/A',
    ],
    datasets: [
      {
        data: [medicaidNum, privateNum],
        backgroundColor: ['#FF7A45', '#73D13D'],
        hoverBackgroundColor: ['#FF6400', 'green'],
        borderWidth: 0,
      },
    ],
  };

  const innerContent = (
    <div className="flex flex-col gap-2 text-center">
      <div>
        <Typography className="text-neutral-9" variant="footnote-description">
          Medicaid
        </Typography>
        <Typography className="text-volcano-6" variant="footnote-description">
          {medicaidPct === null ? 'N/A' : (medicaidPct ?? 0).toFixed(1) + '%'}
        </Typography>
      </div>
      <div>
        <Typography className="text-neutral-9" variant="footnote-description">
          Private
        </Typography>
        <Typography
          className="text-polar-green-7"
          variant="footnote-description"
        >
          {privatePct === null ? 'N/A' : (privatePct ?? 0).toFixed(1) + '%'}
        </Typography>
      </div>
    </div>
  );

  return (
    // The row pins this card to 236px to match Overall Occupancy beside it. The counts
    // block below the donut is two lines (numbers + "residents"), so the header relies on
    // the flex gap alone rather than an extra pb-3 -- otherwise the caption overflows the
    // bottom border.
    <div className="border-neutral-5 flex w-[200px] flex-col items-center gap-1 border bg-white p-3">
      <div className="flex items-center gap-x-2">
        <Typography variant="h5" weight="medium">
          Payer Distribution
        </Typography>
        <TooltipElement content="Share of residents paying privately vs via Medicaid" />
      </div>

      <div className="mt-auto mb-auto px-3">
        {isLoading ? (
          <Loader />
        ) : (
          <DonutChart
            className="flex h-[138px] w-[138px] items-center justify-center"
            data={chartData}
            innerContent={innerContent}
            additionalOptions={{
              plugins: {
                tooltip: {
                  callbacks: {
                    label: (context) => {
                      const raw = context.raw as number;
                      return `${raw.toFixed(1)}% of residents`;
                    },
                  },
                },
              },
            }}
          />
        )}
      </div>
      {!isLoading && (
        // Private / Medicaid resident counts, with a single shared caption beneath the pair.
        <div className="flex flex-col items-center text-center">
          <div className="flex items-center gap-2">
            <Typography variant="body" className="text-polar-green-7">
              {privateCountVal}
            </Typography>
            /
            <Typography variant="body" className="text-volcano-6">
              {medicaidCountVal}
            </Typography>
          </div>
          <Typography className="text-neutral-8" variant="footnote-description">
            residents
          </Typography>
        </div>
      )}
    </div>
  );
};
